import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { OrderLog } from "domains/order/agreggates/order/Order";
import { IOrderRepositoryDB } from "domains/order/interfaces/repositories/IOrderRepositoryDB";
import {
  adjustSitefPayment,
  buildSitefMetadata,
} from "Infrastructure/services/reactNativeServices/ReactNativePaymentService";
import { Payment } from "modules/order/domain/models/Payment";
import {
  ackPayment,
  getPendingPayment,
  msitefOperation,
  PendingNativePayment,
} from "modules/reactNativeBridge/ReactNativeBridge";
import { isPaymentInFlight } from "../inFlightPayments";

export type RecoverPendingPaymentOutcome =
  | "none"
  | "wait"
  | "already-approved"
  | "order-not-found"
  | "not-approved"
  | "no-result"
  | "approved";

export interface IRecoverPendingPaymentResult {
  outcome: RecoverPendingPaymentOutcome;
  orderId?: string;
  friendlyId?: string;
  message?: string;
  /** Só em "wait": pagamento de uma página anterior, ainda sem resultado (o totem deve aguardar) */
  blocking?: boolean;
  /** Só em "wait" com o m-SiTef já fechado: quanto falta para a consulta 130 */
  remainingMs?: number;
  /** Só em "approved": pedido do meep-orders e pagamento para retomar o fluxo (o ack fica com quem retoma) */
  order?: OrderLog;
  payment?: Payment;
}

/** O m-SiTef pode continuar aberto depois que o WebView recarrega: PENDING recente ainda não é "sem retorno" */
const MIN_PENDING_AGE_MS = 3 * 60 * 1000;

const ORPHANS_KEY = "@meep/msitefOrphanPayments";

const saveOrphan = (pending: PendingNativePayment) => {
  try {
    const items = JSON.parse(localStorage.getItem(ORPHANS_KEY) ?? "[]");
    localStorage.setItem(ORPHANS_KEY, JSON.stringify([...items, { ...pending, savedAt: Date.now() }]));
  } catch {
    // Só registro auxiliar para conciliação
  }
};

const describeRequest = (pending: PendingNativePayment) => {
  const request = pending.request ?? {};
  const valor = request.valor ? `R$ ${(Number(request.valor) / 100).toFixed(2)}` : "-";
  return `cupom ${request.numeroCupom ?? "-"}, valor ${valor}, data ${request.data ?? "-"} ${request.hora ?? ""}`.trim();
};

/**
 * Lê o pagamento m-SiTef que o app Android gravou em disco quando caiu (ou teve o WebView
 * recarregado) com o m-SiTef aberto.
 *
 * - RESULT aprovado: devolve pedido e pagamento ("approved") para a tela retomar o fluxo normal.
 * - RESULT negado/cancelado: encerra o pedido como falha.
 * - PENDING antigo (o m-SiTef não devolveu nada): roda a consulta 130 e marca o pedido para conciliação.
 */
export const RecoverPendingPaymentUseCase = async (
  localOrderRepository: IOrderRepositoryDB,
  kiosk: Kiosk
): Promise<IRecoverPendingPaymentResult> => {
  const pending = await getPendingPayment();
  if (!pending) {
    return { outcome: "none" };
  }

  // Esta página ainda está conduzindo o pagamento (ex.: modal da NFC-e aberto)
  if (isPaymentInFlight(pending.operationId)) {
    return { outcome: "wait", orderId: pending.operationId };
  }
  // m-SiTef ainda aberto com o pagamento de uma página recarregada: o resultado vai chegar
  if (pending.inProgress) {
    return { outcome: "wait", orderId: pending.operationId, blocking: true };
  }
  const remainingMs = MIN_PENDING_AGE_MS - (Date.now() - pending.startedAt);
  if (pending.status === "PENDING" && remainingMs > 0) {
    return { outcome: "wait", orderId: pending.operationId, blocking: true, remainingMs };
  }

  const order = await localOrderRepository.findById(pending.operationId);
  if (!order) {
    saveOrphan(pending);
    await ackPayment(pending.operationId);
    return {
      outcome: "order-not-found",
      orderId: pending.operationId,
      message: `Pagamento m-SiTef sem pedido no totem (${describeRequest(pending)})`,
    };
  }

  const base = { orderId: order.id, friendlyId: order.friendlyId };

  if (order.paymentApproved) {
    await ackPayment(pending.operationId);
    return { ...base, outcome: "already-approved" };
  }

  if (pending.status === "PENDING" || !pending.payment) {
    let pendencias = "não consultada";
    try {
      const result130 = await msitefOperation("130", buildSitefMetadata(kiosk));
      const tipoCampos = JSON.parse(result130?.raw?.TIPO_CAMPOS ?? "{}");
      pendencias = `CODRESP ${result130?.code}, pendências ${tipoCampos?.["210"]?.[0] ?? "?"}`;
    } catch (_error) {
      const error = _error as any;
      pendencias = `falhou: ${error?.message ?? error}`;
    }
    const message = `Pagamento sem retorno do m-SiTef: conferir na conciliação (${describeRequest(pending)}; consulta 130: ${pendencias})`;
    await localOrderRepository.add(order, message);
    saveOrphan(pending);
    await ackPayment(pending.operationId);
    return { ...base, outcome: "no-result", message };
  }

  const payment = pending.payment as unknown as Payment;
  if (!payment.success) {
    const message = `Pagamento não aprovado (retorno recuperado): ${(payment as any).message ?? payment.errorMessage ?? ""}`;
    await localOrderRepository.add({ ...order, payment } as OrderLog, message);
    await ackPayment(pending.operationId);
    return { ...base, outcome: "not-approved", message };
  }

  return {
    ...base,
    outcome: "approved",
    order,
    payment: adjustSitefPayment(payment as any) as unknown as Payment,
  };
};
